import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { capture, reset as resetAnalytics } from '../../analytics/posthog';
import { useProductsContext } from '../../context/ProductsContext';
import { useNavBadges } from '../../hooks/useNavBadges';
import { clearStaleInteractionLock } from '../../utils/msalInteractionLock';
import { usePWAMode } from '../../hooks/usePWAMode';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import HelpMenu from './HelpMenu';
import {
  Mail,
  CheckSquare,
  ClipboardCheck,
  FolderKanban,
  Calendar,
  Building2,
  Users,
  BarChart3,
  Trophy,
  Handshake,
  FileText,
  Briefcase,
  Package,
  ShoppingCart,
  Truck,
  TrendingUp,
  LayoutDashboard,
  Upload,
  Download,
  Bot,
  UserCog,
  Inbox,
  Target,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  Newspaper,
  Menu,
  X,
  Search,
  Receipt,
  Monitor,
  DollarSign,
  LayoutGrid,
  Globe,
  Calculator,

  Eye,
  BarChart2,
  Mountain,
  Lightbulb,
  FlaskConical,
  Sparkles,
  Tag,
  Database,
  Mic,
  Waypoints,
  Network,
  Map as MapIcon,
  ClipboardList,
} from 'lucide-react';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

// CSS-variable-backed style objects — keeps dark-mode readiness
const styles = {
  sidebarBg: { backgroundColor: 'var(--sidebar-bg)' },
  activeBar: { backgroundColor: 'var(--sidebar-active-bar)' },
  avatar: { backgroundColor: 'var(--color-teal)' },
};

export default function Sidebar({ onOpenSearch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { isCalculator, permissions, moduleToggles } = useProductsContext();
  const isPWA = usePWAMode();
  const { isOffline } = useOnlineStatus();
  const pwaOffline = isPWA && isOffline;
  const canAccessSales = permissions.canAccessSales || false;
  const canAccessOps = permissions.canAccessOps || false;
  const canAccessForecasts = permissions.canAccessForecasts || false;
  const canViewInventory = permissions.canViewInventory || false;
  const canAccessAdmin = permissions.canAccessAdmin || false;
  const canViewSalesLeaderboard = permissions.canViewSalesLeaderboard || false;
  const canViewSalesDashboard = permissions.canViewSalesDashboard || false;
  const canViewCommissions = permissions.canViewCommissions || false;
  const canManageCommissions = permissions.canManageCommissions || false;
  const canAccessInvestors = permissions.canAccessInvestors || false;
  // Module toggles: default to enabled if not explicitly set to false
  const isModuleOn = (key) => moduleToggles[key] !== false;
  // Sub-page toggles: enabled if both parent module AND sub-page are on
  const isSubPageOn = (parentKey, subKey) => isModuleOn(parentKey) && moduleToggles[subKey] !== false;
  // The weekly check-in and the Monday pre-read are both generally available —
  // the pre-read email links the whole company to /activ/pre-read.
  const { badges } = useNavBadges();
  // Sync combined badge count to PWA app badge (home screen icon)
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    const dealsVisible = moduleToggles.sales !== false && moduleToggles['sales.deals'] !== false;
    const total =
      (badges.tasks_overdue || 0) +
      (dealsVisible ? (badges.deals_no_next_step || 0) : 0) +
      (badges.quotes_pending || 0);
    if (total > 0) {
      navigator.setAppBadge(total).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [badges, moduleToggles]);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const account = instance.getActiveAccount();
  const userName = account?.name || account?.username || 'User';
  const initials = userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const pathname = location.pathname;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Escape key closes mobile menu
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen]);


  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const toggleSection = useCallback((name) => {
    setCollapsed(prev => {
      if (prev) {
        // Expand sidebar first, then open this section
        try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false'); } catch { /* ignore */ }
        setExpandedSections(s => ({ ...s, [name]: true }));
        return false;
      }
      setExpandedSections(s => ({ ...s, [name]: !s[name] }));
      return prev;
    });
  }, []);

  const handleNav = useCallback((path) => {
    navigate(path);
    setMobileOpen(false);
  }, [navigate]);

  // logoutRedirect is the one interactive MSAL call in the app that does not go
  // through msalTokenManager, so it never had that module's lock recovery. It
  // contends for the same single interaction lock as every token request, so a
  // Graph consent popup left open — or a stale flag — makes it reject with
  // `interaction_in_progress`. With no catch that surfaced as an unhandled
  // rejection, and the user-visible symptom was the worst kind: clicking
  // "Sign out" did nothing at all, silently, with no way to tell it had failed.
  //
  // Clear the orphaned lock and retry once, mirroring msalTokenManager's
  // runInteractive(). If the second attempt also fails the lock is real, and a
  // full reload is the honest remedy — it drops any in-page interaction and
  // sessionStorage-held lock, after which sign-out works.
  const handleLogout = useCallback(async () => {
    capture('user_signed_out');
    resetAnalytics();
    const request = { postLogoutRedirectUri: window.location.origin };
    try {
      await instance.logoutRedirect(request);
    } catch {
      clearStaleInteractionLock();
      try {
        await instance.logoutRedirect(request);
      } catch {
        // Never leave the user stuck on a dead button. Reloading clears the
        // interaction state; MSAL's own message here is only a docs URL under
        // v5, so there is nothing worth showing them.
        window.location.reload();
      }
    }
  }, [instance]);

  const isActive = useCallback((path, exactMatch = false) => {
    if (exactMatch || path === '/') return pathname === path;
    return pathname.startsWith(path);
  }, [pathname]);

  // --- Sidebar content (shared between desktop and mobile) ---
  const renderContent = (isMobile = false) => {
    const isCollapsed = collapsed && !isMobile;

    return (
      <div className="flex flex-col h-full">
        {/* Logo area - hidden on mobile since mobile top bar already shows logo */}
        {!isMobile && (
          <div className={`flex flex-shrink-0 items-center px-4 h-16 ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
            <button onClick={() => handleNav('/')} className="flex items-center gap-3 min-w-0">
              <img
                src="/Activ-Energy_Lettermark_White.png"
                alt="Activ Energy"
                className="h-8 w-8 object-contain"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <span style={{ display: 'none' }} className={`text-lg font-bold text-white items-center ${isCollapsed ? 'hidden' : ''}`}>
                AE
              </span>
            </button>
            {!isCollapsed && (
              <button
                onClick={toggleCollapsed}
                className="ml-auto p-2 rounded-ae-xs transition-colors flex-shrink-0"
                style={{ color: 'var(--sidebar-text)' }}
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>
        )}

        {/* Scrollable nav area */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5" aria-label="Main navigation">
          {/* Universal search trigger */}
          {onOpenSearch && (
            <button
              onClick={onOpenSearch}
              className={`flex items-center w-full rounded-ae-xs text-sm transition-colors mb-1 ${
                isCollapsed ? 'justify-center min-h-[44px]' : 'gap-3 px-3 min-h-[40px]'
              }`}
              style={{
                backgroundColor: 'var(--sidebar-active-bg)',
                color: 'var(--sidebar-text)',
              }}
              title={isCollapsed ? 'Search (⌘K)' : undefined}
              aria-label="Search"
            >
              <Search size={isCollapsed ? 20 : 16} className="flex-shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-70">Search...</span>
                  <kbd
                    className="ae-num text-[10px] px-1.5 py-0.5 rounded-ae-xs opacity-60 flex-shrink-0"
                    style={{ backgroundColor: 'var(--sidebar-divider)' }}
                  >
                    {navigator.platform?.includes('Mac') ? '⌘K' : 'Ctrl+K'}
                  </kbd>
                </>
              )}
            </button>
          )}

          {/* PWA offline mode: limited nav (Home, Deals, Presentations) */}
          {pwaOffline && (
            <>
              <NavItem icon={LayoutDashboard} label="Home" path="/" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              <NavItem icon={Handshake} label="Deals" path="/deals" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
            </>
          )}

          {/* Calculator role: limited nav */}
          {!pwaOffline && isCalculator && (
            <>
              <NavItem icon={Mail} label="Email" path="/emails" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              <NavItem icon={CheckSquare} label="Tasks" path="/tasks" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              <NavItem icon={FolderKanban} label="Projects" path="/projects" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              <NavItem icon={Calendar} label="Calendar" path="/meetings" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              <Divider />
              <NavItem icon={BarChart3} label="Calculator" path="/quotes/new" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
            </>
          )}

          {/* Full nav */}
          {!pwaOffline && !isCalculator && (
            <>
              {/* My Dashboard — top-level home */}
              <NavItem icon={LayoutDashboard} label="My Dashboard" path="/" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />

              <Divider />

              {/* Cross-cutting tools */}
              {isModuleOn('email') && <NavItem icon={Mail} label="Email" path="/emails" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />}
              {isModuleOn('tasks') && <NavItem icon={CheckSquare} label="Tasks" path="/tasks" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} badge={badges.tasks_overdue} />}
              {isModuleOn('requests') && <NavItem icon={Inbox} label="Requests" path="/requests" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} badge={badges.requests_triage} />}
              {isModuleOn('projects') && <NavItem icon={FolderKanban} label="Projects" path="/projects" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />}
              {isModuleOn('calendar') && <NavItem icon={Calendar} label="Calendar" path="/meetings" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />}

              <Divider />

              {/* Sales section */}
              {canAccessSales && isModuleOn('sales') && (
                <NavSection
                  icon={TrendingUp}
                  label="Sales"
                  name="sales"
                  collapsed={isCollapsed}
                  expanded={expandedSections.sales}
                  onToggle={toggleSection}
                  badge={(isSubPageOn('sales', 'sales.deals') ? (badges.deals_no_next_step || 0) : 0) + (isSubPageOn('sales', 'sales.backlog') ? (badges.deal_candidates_new || 0) : 0) + (isSubPageOn('sales', 'sales.quotes') ? (badges.quotes_pending || 0) : 0)}
                >
                  {canViewSalesDashboard && isSubPageOn('sales', 'sales.dashboard') && <NavItem icon={LayoutDashboard} label="Sales Manager" path="/sales-manager" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canViewSalesLeaderboard && isSubPageOn('sales', 'sales.leaderboard') && (
                    <NavItem icon={Trophy} label="Leaderboard" path="/leaderboard" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  )}
                  {isSubPageOn('sales', 'sales.deals') && <NavItem icon={Building2} label="Companies" path="/companies" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('sales', 'sales.opportunities') && <NavItem icon={Target} label="Opportunities" path="/opportunities" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('sales', 'sales.backlog') && <NavItem icon={Sparkles} label="Backlog" path="/deals/backlog" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested badge={badges.deal_candidates_new} />}
                  {isSubPageOn('sales', 'sales.deals') && <NavItem icon={Handshake} label="Deals" path="/deals" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested badge={badges.deals_no_next_step} />}
                  {isSubPageOn('sales', 'sales.deals') && <NavItem icon={Mic} label="Meeting Debrief" path="/meeting-debrief" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('sales', 'sales.people') && <NavItem icon={Users} label="People" path="/people" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('sales', 'sales.network') && <NavItem icon={Network} label="Network" path="/network" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('sales', 'sales.quotes') && <NavItem icon={FileText} label="Quotes" path="/quotes" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested badge={badges.quotes_pending} />}
                  {isSubPageOn('sales', 'sales.price_list') && <NavItem icon={Tag} label="Price List" path="/price-list" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canViewCommissions && isSubPageOn('sales', 'sales.commissions') && (
                    <NavItem icon={DollarSign} label="Commissions" path="/commissions" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  )}
                </NavSection>
              )}

              {/* Ops section — Jobs, Vendors, and Purchase Orders are visible to
                  every authenticated user; Inventory / Ops Dashboard remain
                  gated to ops or admin. Invoices moved to the Finance section. */}
              {isModuleOn('ops') && (
                <NavSection
                  icon={Briefcase}
                  label="Ops"
                  name="ops"
                  collapsed={isCollapsed}
                  expanded={expandedSections.ops}
                  onToggle={toggleSection}
                >
                  {isSubPageOn('ops', 'ops.jobs') && <NavItem icon={Briefcase} label="Jobs" path="/jobs" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('ops', 'ops.logistics') && <NavItem icon={Waypoints} label="Logistics" path="/logistics" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {(canViewInventory || canAccessForecasts) && isSubPageOn('ops', 'ops.ops_map') && <NavItem icon={MapIcon} label="Operations Map" path="/ops-map" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  <NavItem icon={Building2} label="Vendors" path="/vendors" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  {isSubPageOn('ops', 'ops.purchase_orders') && <NavItem icon={ShoppingCart} label="Purchase Orders" path="/purchase-orders" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('ops', 'ops.bols') && <NavItem icon={Truck} label="Bills of Lading" path="/bols" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {(canViewInventory || canAccessForecasts) && isSubPageOn('ops', 'ops.inventory') && <NavItem icon={Package} label="Inventory" path="/inventory" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {(canViewInventory || canAccessForecasts) && isSubPageOn('ops', 'ops.inventory') && <NavItem icon={BarChart3} label="Nano Forecast" path="/reports/customer-forecast" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {(canViewInventory || canAccessForecasts) && isSubPageOn('ops', 'ops.inventory') && <NavItem icon={ClipboardList} label="Nano Planning" path="/reports/nano-planning" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                </NavSection>
              )}

              {/* Partnerships */}
              {isModuleOn('partnerships') && (
                <NavItem icon={Handshake} label="Partnerships" path="/partnerships" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
              )}

              {/* Science — customer technical requests + lab test catalog */}
              {isModuleOn('science') && (
                <NavSection
                  icon={FlaskConical}
                  label="Science"
                  name="science"
                  collapsed={isCollapsed}
                  expanded={expandedSections.science}
                  onToggle={toggleSection}
                >
                  {isSubPageOn('science', 'science.requests') && (
                    <NavItem
                      icon={FlaskConical}
                      label="Requests"
                      path="/science"
                      collapsed={isCollapsed}
                      isActive={() => pathname.startsWith('/science') && !location.search.includes('tab=tests') && !pathname.startsWith('/science/tests')}
                      onClick={handleNav}
                      nested
                    />
                  )}
                  {isSubPageOn('science', 'science.tests') && (
                    <NavItem
                      icon={FlaskConical}
                      label="Tests"
                      path="/science?tab=tests"
                      collapsed={isCollapsed}
                      isActive={() => (pathname === '/science' && location.search.includes('tab=tests')) || pathname.startsWith('/science/tests')}
                      onClick={handleNav}
                      nested
                    />
                  )}
                  {isSubPageOn('science', 'science.coas') && (
                    <NavItem
                      icon={FlaskConical}
                      label="COAs"
                      path="/science/coas"
                      collapsed={isCollapsed}
                      isActive={() => pathname.startsWith('/science/coas')}
                      onClick={handleNav}
                      nested
                    />
                  )}
                </NavSection>
              )}

              {/* Finance section — visible to anyone with commission-write
                  (admin or finance role), so the finance role gets the nav
                  without unlocking the rest of the admin section. */}
              {(canManageCommissions || canAccessInvestors) && (
                <NavSection
                  icon={DollarSign}
                  label="Finance"
                  name="finance"
                  collapsed={isCollapsed}
                  expanded={expandedSections.finance}
                  onToggle={toggleSection}
                >
                  {canManageCommissions && isModuleOn('finance') && isSubPageOn('finance', 'finance.commissions') && <NavItem icon={DollarSign} label="Commissions" path="/admin/commissions" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canManageCommissions && isModuleOn('finance') && isSubPageOn('finance', 'finance.exports') && <NavItem icon={Download} label="Exports" path="/finance/exports" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canManageCommissions && isModuleOn('finance') && isSubPageOn('finance', 'finance.invoices') && <NavItem icon={Receipt} label="Invoices" path="/invoices" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canAccessInvestors && isSubPageOn('finance', 'finance.investor_updates') && <NavItem icon={FileText} label="Investor Updates" path="/investor-updates" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {canAccessInvestors && isSubPageOn('finance', 'finance.investor_people') && <NavItem icon={Users} label="Investor People" path="/investor-updates/people" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested exactMatch />}
                </NavSection>
              )}

              {/* Activ section — weekly check-in visible to all, EOS tools admin-only */}
              {isModuleOn('activ') && (
                <NavSection
                  icon={Target}
                  label="Activ"
                  name="activ"
                  collapsed={isCollapsed}
                  expanded={expandedSections.activ}
                  onToggle={toggleSection}
                >
                  <NavItem icon={ClipboardCheck} label="Weekly Check-In" path="/activ/checkin" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  <NavItem icon={FileText} label="Monday Pre-Read" path="/activ/pre-read" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  {isSubPageOn('activ', 'activ.scorecard') && <NavItem icon={BarChart2} label="Scorecard" path="/activ/scorecard" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('activ', 'activ.rocks') && <NavItem icon={Mountain} label="Rocks" path="/activ/rocks" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                  {isSubPageOn('activ', 'activ.meetings') && <NavItem icon={Users} label="Level 10" path="/activ/meetings" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />}
                </NavSection>
              )}

              {/* Admin section */}
              {canAccessAdmin && (
                <NavSection
                  icon={UserCog}
                  label="Admin"
                  name="admin"
                  collapsed={isCollapsed}
                  expanded={expandedSections.admin}
                  onToggle={toggleSection}
                >
                  <NavItem icon={Bot} label="AI Instructions" path="/admin/instructions" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  <NavItem icon={UserCog} label="User Management" path="/admin/users" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  <NavItem icon={Inbox} label="Email Inbox" path="/admin/emails" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  <NavItem icon={LayoutGrid} label="Module Toggle" path="/admin/modules" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                  <NavItem icon={Building2} label="Departments" path="/admin/departments" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} nested />
                </NavSection>
              )}
            </>
          )}
        </nav>

        {/* Bottom section */}
        <div className="flex-shrink-0 px-3 py-3 space-y-0.5" style={{ borderTop: '1px solid var(--sidebar-divider)' }}>
          {/* What's New — hidden in PWA offline mode and for quote builder users */}
          {!pwaOffline && !isCalculator && (
            <NavItem icon={Newspaper} label="What's New" path="/updates" collapsed={isCollapsed} isActive={isActive} onClick={handleNav} />
          )}

          {/* Help — same visibility rules as What's New */}
          {!pwaOffline && !isCalculator && (
            <HelpMenu collapsed={isCollapsed} isMobile={isMobile} onLinkClick={() => setMobileOpen(false)} />
          )}

          {/* User row */}
          <div className={`flex items-center gap-3 px-3 min-h-[44px] ${isCollapsed ? 'justify-center' : ''}`}>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={styles.avatar}
            >
              {initials}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate font-medium">{userName}</p>
              </div>
            )}
            {!isCollapsed && (
              <button
                onClick={handleLogout}
                className="p-2 rounded-ae-xs transition-colors flex-shrink-0"
                style={{ color: 'var(--sidebar-text)' }}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>

          {/* Collapse/expand toggle at bottom */}
          {!isMobile && isCollapsed && (
            <button
              onClick={toggleCollapsed}
              className="flex items-center justify-center w-full min-h-[44px] rounded-ae-xs transition-colors"
              style={{ color: 'var(--sidebar-text)' }}
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <PanelLeft size={18} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile top bar */}
      <div
        className="sm:hidden fixed top-0 left-0 right-0 z-40 px-3 flex items-center gap-3 min-h-[52px] pt-[env(safe-area-inset-top)]"
        style={{ ...styles.sidebarBg, borderBottom: '1px solid var(--sidebar-divider)' }}
      >
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-white rounded-ae-xs transition-colors"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <button onClick={() => handleNav('/')} className="flex items-center gap-2">
          <img
            src="/Activ-Energy_Lettermark_White.png"
            alt="Activ Energy"
            className="h-7"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <span style={{ display: 'none' }} className="text-sm font-bold text-white items-center">
            Activ OS
          </span>
        </button>
        {onOpenSearch && pathname !== '/' && (
          <button
            onClick={onOpenSearch}
            className="ml-auto p-2 text-white rounded-ae-xs transition-colors"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
        )}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sm:hidden fixed inset-0 z-30"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <div
        className={`sm:hidden fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-[250ms] ease-out pt-[calc(52px+env(safe-area-inset-top))] ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={styles.sidebarBg}
      >
        {renderContent(true)}
      </div>

      {/* Desktop/tablet sidebar */}
      <aside
        className={`hidden sm:flex flex-col flex-shrink-0 h-screen sticky top-0 transition-[width] duration-200 ease-out overflow-hidden ${
          collapsed ? 'w-16' : 'w-60'
        }`}
        style={styles.sidebarBg}
      >
        {renderContent(false)}
      </aside>
    </>
  );
}

// --- Sub-components ---

function Divider() {
  return <div className="my-3" style={{ borderTop: '1px solid var(--sidebar-divider)' }} />;
}

function NavItem({ icon: Icon, label, path, collapsed, isActive: isActiveFn, onClick, nested = false, exactMatch = false, badge = 0 }) {
  const active = isActiveFn(path, exactMatch);
  const badgeLabel = badge > 0 ? `${label} (${badge})` : label;

  if (collapsed) {
    return (
      <button
        onClick={() => onClick(path)}
        className="relative flex items-center justify-center w-full min-h-[44px] rounded-ae-xs text-sm transition-colors"
        style={{
          color: active ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
          backgroundColor: active ? 'var(--sidebar-active-bg)' : undefined,
        }}
        title={badgeLabel}
        aria-label={badgeLabel}
      >
        {active && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r" style={styles.activeBar} />}
        <span className="relative">
          <Icon size={20} />
          {badge > 0 && (
            <span className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-ae-pill bg-activ-red text-white text-[9px] font-semibold px-0.5 ae-num">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={() => onClick(path)}
      className={`relative flex items-center gap-3 min-h-[44px] rounded-ae-xs text-sm transition-colors w-full ${
        nested ? 'pl-10 pr-3' : 'px-3'
      }`}
      style={{
        color: active ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
        backgroundColor: active ? 'var(--sidebar-active-bg)' : undefined,
        fontWeight: active ? 500 : 400,
      }}
      aria-label={badgeLabel}
    >
      {active && !nested && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r" style={styles.activeBar} />}
      <Icon size={nested ? 16 : 20} className="flex-shrink-0" />
      <span className="truncate min-w-0">{label}</span>
      {badge > 0 && (
        <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-ae-pill bg-activ-red text-white text-[10px] font-semibold px-1 ae-num">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

function NavSection({ icon: Icon, label, name, collapsed, expanded, onToggle, badge = 0, children }) {
  if (collapsed) {
    return (
      <button
        onClick={() => onToggle(name)}
        className="relative flex items-center justify-center w-full min-h-[44px] rounded-ae-xs text-sm transition-colors"
        style={{ color: 'var(--sidebar-text)' }}
        title={badge > 0 ? `${label} (${badge})` : label}
        aria-label={badge > 0 ? `${label} (${badge})` : label}
      >
        <span className="relative">
          <Icon size={20} />
          {badge > 0 && (
            <span className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-ae-pill bg-activ-red text-white text-[9px] font-semibold px-0.5 ae-num">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <div>
      <button
        onClick={() => onToggle(name)}
        className="flex items-center gap-3 px-3 min-h-[44px] rounded-ae-xs text-sm transition-colors w-full"
        style={{
          color: expanded ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
          fontWeight: expanded ? 500 : 400,
        }}
        aria-expanded={expanded}
      >
        <Icon size={20} className="flex-shrink-0" />
        <span className="truncate flex-1 text-left">{label}</span>
        {!expanded && badge > 0 && (
          <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-ae-pill bg-activ-red text-white text-[10px] font-semibold px-1 ae-num">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {expanded ? <ChevronDown size={14} className="flex-shrink-0" /> : <ChevronRight size={14} className="flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
